import { useState } from 'react'
import { PieChart, legendClasses } from '@mui/x-charts'

import { HUMAN_STACK_REVRESE_LOOKUP, STACK } from 'types/deck'
import { stackOptions } from 'components/deckPage/filtersAndControls/DeckGroupByDropdown'

import { useAppSelector } from 'app-redux/hooks'
import { selectInPriceCards, selectStacksWithOptions } from 'app-redux/deck/selectors'

import ChangePriceSource from 'components/deckPage/filtersAndControls/ChangePriceSource'
import PriceTable from 'components/deckPage/header/DeckPrice/PriceTable'
import BarChart from 'components/deckPage/charts/BarChart'
import Checkbox from 'components/formElements/Checkbox'
import ArchidektDropdown from 'components/elements/ArchidektDropdown'
import Icon from 'components/elements/Icon'

import { getPriceStringFromFloat } from 'utils/Price'

import styles from './deckPriceBreakdown.module.scss'

const DeckPriceBreakdown = () => {
  const stack = useAppSelector(state => state.deck.stack)

  const [asBarChart, setAsBarChart] = useState(false)
  const [selectedGroupBy, setSelectedGroupBy] = useState(stack)

  const { mainDeck: selectedGroups } = useAppSelector(state =>
    selectStacksWithOptions(state, { stackTypeOverride: selectedGroupBy, dontBreakoutCommander: true }),
  )

  const { mainDeck: mainDeckByPrice } = useAppSelector(state =>
    selectStacksWithOptions(state, { stackTypeOverride: STACK.PRICE, dontBreakoutCommander: true }),
  )

  const cards = useAppSelector(selectInPriceCards)
  const priceSource = useAppSelector(state => state.active.priceSource)

  const deckBreakdown = selectedGroups
    .filter(stackType => stackType.includedInPrice)
    .map(stackType => ({
      label: stackType.name,
      value: stackType.price,
      count: stackType.price,
    }))

  const priceBreakdown = mainDeckByPrice
    .filter(stackType => stackType.includedInPrice)
    .map(stackType => ({
      label: stackType.name,
      value: stackType.quantity,
      count: stackType.quantity,
    }))

  return (
    <div className={styles.content}>
      <PriceTable cards={cards} priceSource={priceSource[0]} />
      <PriceTable cards={cards} priceSource={priceSource[1]} />
      <PriceTable cards={cards} priceSource={priceSource[2]} />

      <div className={styles.header}>
        <ChangePriceSource />
      </div>

      <div className={styles.charts}>
        <div className={styles.chart}>
          <div className={styles.chartHeader}>
            <div className={styles.customHeader}>
              <span className={styles.label}>Price breakdown by</span>
              <ArchidektDropdown
                menuClassName={styles.customDropdown}
                options={stackOptions.map(opt => ({ label: opt.text, onClick: () => setSelectedGroupBy(opt.value) }))}>
                <button className={styles.trigger}>
                  {HUMAN_STACK_REVRESE_LOOKUP[selectedGroupBy]}
                  <Icon name="chevron down" />
                </button>
              </ArchidektDropdown>
            </div>

            <Checkbox label="Show as bar chart" checked={asBarChart} onChange={() => setAsBarChart(!asBarChart)} />
          </div>

          {asBarChart ? (
            <BarChart
              data={deckBreakdown}
              handleFormatCount={value => getPriceStringFromFloat(value, priceSource[0])}
              containerClassName={styles.barChart}
              labelContainerClassName={styles.chartLabels}
            />
          ) : (
            <div className={styles.piChart}>
              <PieChart
                skipAnimation
                margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
                width={180}
                series={[
                  {
                    innerRadius: 0,
                    outerRadius: 90,
                    startAngle: -45,
                    data: deckBreakdown,
                    valueFormatter: item => `${getPriceStringFromFloat(item.value, priceSource[0])}`,
                  },
                ]}
                sx={{
                  [`& .${legendClasses.series}`]: {
                    fontSize: '16px',
                    color: 'var(--color)',
                  },
                }}
                slotProps={{
                  legend: {
                    direction: 'horizontal',
                    position: {
                      vertical: 'bottom',
                    },
                  },
                }}
              />
            </div>
          )}
        </div>

        <div className={styles.chart}>
          <h4 className={styles.chartHeader}>Cards per price group</h4>

          <BarChart
            data={priceBreakdown}
            containerClassName={styles.barChart}
            labelContainerClassName={styles.chartLabels}
          />
        </div>
      </div>
    </div>
  )
}

export default DeckPriceBreakdown
